import { Component, input, output } from '@angular/core';


/**
 * Mobile-Responsive Feature Header Component
 *
 * This component provides a fully responsive header with:
 * - Adaptive layout: stacks vertically on mobile, horizontal on desktop
 * - Responsive text sizing: smaller on mobile, larger on desktop
 * - Flexible action area: wraps on mobile, single line on desktop
 * - Touch-friendly buttons: larger touch targets on mobile
 * - Content truncation: prevents overflow with ellipsis
 *
 * Mobile Behavior:
 * - Layout switches to column on screens < 640px (sm breakpoint)
 * - Back button uses smaller size automatically
 * - Title text scales down appropriately
 * - Actions wrap and align to the right on mobile
 * - Proper spacing and touch targets for mobile interaction
 *
 * Usage Examples:
 *
 * // Basic usage
 * <lib-feature-header title="User Management" />
 *
 * // With back button and actions
 * <lib-feature-header
 *   title="Edit User"
 *   [showBackButton]="true"
 *   (backClick)="goBack()">
 *   <button class="btn btn-primary">Save</button>
 *   <button class="btn btn-secondary">Cancel</button>
 * </lib-feature-header>
 *
 * // Mobile-optimized actions (recommended pattern)
 * <lib-feature-header title="Long Title That Might Truncate">
 *   <!-- Actions will wrap on mobile and maintain proper spacing -->
 *   <button class="btn btn-sm sm:btn-md">Edit</button>
 *   <button class="btn btn-sm sm:btn-md">Delete</button>
 * </lib-feature-header>
 */
@Component({
  standalone: true,
  imports: [],
  selector: 'lib-feature-header',
  template: `
    <!-- Mobile-first responsive container -->
    <div
      class="flex flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 bg-slate-600 p-3 rounded-t-lg "
    >
      <!-- Back button with title -->
      <div class="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <span class="text-normal font-bold text-slate-100 ">{{ title() }}</span>

        @if (isLoading()) {
        <div
          class="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"
        ></div>
        }
      </div>

      <!-- Actions slot - responsive layout -->
      @if (hasActions) {
      <div
        class="flex items-center justify-end sm:justify-start gap-2 sm:gap-3 flex-wrap sm:flex-nowrap flex-shrink-0"
      >
        <ng-content></ng-content>
      </div>
      }

      <!-- Close button -->
      @if (!hideCloseButton()) {
      <div>
        <button
          aria-label="Close"
          class="btn btn-sm sm:btn-md btn-circle btn-ghost hover:text-orange-200 text-white"
          (click)="onCloseClick()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 sm:h-5 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      }
    </div>
  `,
})
export class FeatureHeaderComponent {
  // Input signals
  title = input.required<string>();
  titleSize = input<'sm' | 'md' | 'lg' | 'xl'>('xl');
  titleColor = input<'gray' | 'black' | 'orange'>('gray');
  isLoading = input<boolean>(false);
  hideCloseButton = input<boolean>(false);

  // Output signals
  closeClick = output<void>();

  // Check if actions content is projected
  hasActions = true; // We'll assume actions are always possible for flexibility

  // Computed title classes with responsive sizing
  titleClasses(): string {
    const sizeClasses = {
      sm: 'text-base sm:text-lg',
      md: 'text-lg sm:text-xl',
      lg: 'text-xl sm:text-2xl',
      xl: 'text-xl sm:text-2xl md:text-3xl',
    };

    const colorClasses = {
      gray: 'text-gray-700',
      black: 'text-gray-900',
      orange: 'text-orange-600',
    };

    return `font-bold ${sizeClasses[this.titleSize()]} ${
      colorClasses[this.titleColor()]
    }`;
  }

  onCloseClick(): void {
    this.closeClick.emit();
  }
}
